/**
 * VowsEra - API Service
 * Axios instance with interceptors for API calls
 */

import axios from 'axios';
import { MARKET } from '@utils/market';
import { getActingWedding, stopActing, ACTING_HEADER } from '@utils/actingWedding';

// Get API URL from environment
// Vite loads: .env, .env.local, .env.[mode], .env.[mode].local
// If VITE_API_URL is not set, fall back to localhost
const envUrl = import.meta.env.VITE_API_URL;
const baseURL = envUrl ? envUrl.replace(/\/+$/, '') : 'http://localhost:8000';

const api = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Convert camelCase string to snake_case.
 * e.g. "partnerFirstName" → "partner_first_name"
 *
 * Handles consecutive caps as one segment so abbreviations don't get
 * each letter underscore-separated:
 *   "maxUploadSizeMb" → "max_upload_size_mb"   (good)
 *   "URLPath"         → "url_path"             (CAPS+Pa boundary)
 *   "userID"          → "user_id"
 * The two-pass approach mirrors the standard regex idiom for this — the
 * first pass inserts `_` at every lower→CAP boundary, the second at every
 * CAPS→Cap boundary, then we lowercase.
 */
const toSnake = (str) => str
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();

/**
 * Convert snake_case string to camelCase.
 * e.g. "partner_first_name" → "partnerFirstName"
 */
const toCamel = (str) => str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

/**
 * Recursively convert all object keys from camelCase to snake_case.
 * Skips arrays of primitives, handles nested objects.
 */
const keysToSnake = (obj) => {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(keysToSnake);
  if (obj instanceof FormData || obj instanceof Blob || obj instanceof File) return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[toSnake(key)] = (typeof value === 'object' && value !== null && !(value instanceof Date))
      ? keysToSnake(value)
      : value;
  }
  return result;
};

/**
 * Recursively convert all object keys from snake_case to camelCase.
 * Handles nested objects and arrays.
 */
const keysToCamel = (obj) => {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (obj instanceof FormData || obj instanceof Blob || obj instanceof File) return obj;
  if (Array.isArray(obj)) return obj.map(keysToCamel);
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[toCamel(key)] = (typeof value === 'object' && value !== null && !(value instanceof Date))
      ? keysToCamel(value)
      : value;
  }
  return result;
};

// Request interceptor - Add auth token + convert camelCase → snake_case
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Which regional site this is (vowsera.com / vowsera.in). The backend
    // uses it for new records, plan limits, prices and marketplace listings.
    config.headers['X-VowsEra-Market'] = MARKET;

    // A planner working inside a client's wedding in this tab. The server
    // checks their assignment on every request; this only names the wedding.
    const acting = getActingWedding();
    if (acting) config.headers[ACTING_HEADER] = acting.id;

    // Convert request body keys from camelCase to snake_case
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.data = keysToSnake(config.data);
    }

    // Convert query params keys from camelCase to snake_case
    if (config.params && typeof config.params === 'object') {
      config.params = keysToSnake(config.params);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Single-flight token refresh.
 *
 * A dashboard mount fans out to half a dozen endpoints at once, so several
 * requests can 401 simultaneously. Each used to fire its own /auth/refresh —
 * and because the backend rotates the refresh token, the first call
 * invalidated the token the others were still holding, so every request after
 * the first failed and the user was thrown back to /login. Sharing one
 * in-flight promise means exactly one refresh happens and everyone waits on it.
 */
let refreshPromise = null;

function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const pending = (async () => {
    const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
    if (!refreshToken) throw new Error('No refresh token available');

    // Uses raw axios (not the shared `api` instance) to skip the request
    // interceptor — which would normally snake_case the body. Without that
    // conversion, the server's validator (`refresh_token` => 'required') sees
    // no field and returns 422 "The refresh token field is required.", which
    // the SPA surfaces as an alert + then bounces to /login. Send the
    // snake_case key directly so the call works regardless of whether the
    // interceptor is active.
    const response = await axios.post(
      `${api.defaults.baseURL}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Accept': 'application/json' } },
    );

    const { accessToken, refreshToken: newRefreshToken } = response.data.data;
    // Always persist to localStorage. Every reader in the app — this
    // interceptor, AuthContext, EchoContext, aiConciergeService — checks
    // localStorage FIRST, so writing a refreshed token to sessionStorage left
    // the stale localStorage copy winning every subsequent request: an endless
    // 401 -> refresh loop that ended in a forced logout once the rotated
    // refresh token was rejected. `rememberMe` is a UI preference (prefill the
    // email field), not a storage selector — AuthContext.login() already
    // writes to localStorage unconditionally.
    localStorage.setItem('accessToken', accessToken);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }
    return accessToken;
  })();

  refreshPromise = pending;
  // Release the slot either way so a later 401 can refresh again. The
  // .catch() here only prevents an unhandled rejection on this bookkeeping
  // chain — the caller still sees the real failure via the returned promise.
  pending
    .catch(() => {})
    .finally(() => { if (refreshPromise === pending) refreshPromise = null; });

  return pending;
}

// Response interceptor - Convert snake_case → camelCase, handle errors, token refresh, & offline sync
api.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      response.data = keysToCamel(response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // ── Offline: queue mutations for background sync ──
    if (!error.response && !navigator.onLine && originalRequest) {
      const method = (originalRequest.method || '').toUpperCase();
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        try {
          const { queueOfflineMutation } = await import('@utils/offlineSync');
          const queued = await queueOfflineMutation(
            originalRequest.url,
            method,
            originalRequest.headers || {},
            originalRequest.data ? JSON.stringify(originalRequest.data) : null
          );
          // Only report an offline save once the write is durably queued.
          // Returning this unconditionally told the user their change was
          // safe even when IndexedDB had rejected it.
          if (queued) {
            return {
              data: { success: true, offline: true, message: 'Saved offline — will sync when connected.' },
              status: 200,
              config: originalRequest
            };
          }
        } catch {
          /* silently handled */
        }
      }
    }

    // ── Account suspended or deactivated ──
    // The API refuses every authenticated request from an account that is
    // not active (EnsureAccountActive). The session is dead — retrying or
    // refreshing cannot revive it — so clear it and send the user to sign
    // in, where the login endpoint explains why. Checked before the 401
    // refresh logic matters: this arrives as a 403, which never refreshes.
    if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_NOT_ACTIVE') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?reason=account-inactive';
      }
      return Promise.reject(error);
    }

    // ── Planner no longer assigned to the client wedding in this tab ──
    // The couple ended the assignment (or it was never confirmed). Leave
    // their wedding and go back to the planner's own list.
    if (error.response?.status === 403 && error.response?.data?.code === 'NOT_ASSIGNED' && getActingWedding()) {
      stopActing();
      window.location.href = '/coordinator/weddings?left=not-assigned';
      return Promise.reject(error);
    }

    // ── NO_WEDDING: user hit a feature that requires a wedding ──
    // Backend returns 404 with {code: 'NO_WEDDING'} so pages can detect it.
    // Broadcast a custom event the app shell listens to (to show a
    // "Create your wedding first" prompt), and turn the rejection into a
    // soft one with a friendly message the calling page can ignore.
    if (error.response?.status === 404 && error.response?.data?.code === 'NO_WEDDING') {
      try {
        window.dispatchEvent(new CustomEvent('vowsera:no-wedding', {
          detail: { message: error.response.data.message, path: originalRequest?.url },
        }));
      } catch { /* no-op */ }
      // Re-throw so service-layer .catch() blocks still run, but make the
      // message clearer for any fallback toasts.
      error.noWedding = true;
      return Promise.reject(error);
    }

    // Only attempt a refresh when there is actually a refresh token to use —
    // a 401 with no token at all is just an unauthenticated request and should
    // surface as-is rather than clearing storage and bouncing to /login.
    const canRefresh = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
    if (error.response?.status === 401 && !originalRequest._retry && canRefresh) {
      originalRequest._retry = true;
      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        // Only force a re-login if the user is on a route that *requires*
        // auth. On public pages (/, /vendors, /vendors/..., /venues, ...) a
        // 401 just means a stale token tried to ride along on an opt-in
        // action like shortlist; bouncing to /login mid-browse is jarring
        // and loses the page they were reading.
        const path = window.location.pathname;
        const requiresAuth = path.startsWith('/dashboard') || path.startsWith('/vendor-portal') || path.startsWith('/admin');
        if (requiresAuth) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
